import { useEffect } from 'react';
import { findBlock } from '../../../utils/blockUtils';
import {
  writeBlocksToClipboardData,
  copyBlocksToClipboard,
  copyBlocksViaExecCommand,
} from '../../../utils/blockClipboard';
import { indentSelectedBlocks } from '../utils/selectionOps';
import {
  CLICK_AWAY_KEEP,
  isLinkedRowChromeTarget,
  isTypingInField,
  selectionTargets,
} from '../utils/selectionDom';

/** Delete, Escape, Ctrl/Cmd+C/X, Tab indent, and click-away for block selection. */
export default function useSelectionHotkeys({
  blocksRef,
  commit,
  selectedBlockId,
  multiSelected,
  multiSelectedRef,
  deleteSelected,
  deleteMultiSelected,
  setSelectedBlockId,
  setMultiSelected,
}) {
  useEffect(() => {
    if (!selectedBlockId && multiSelected.length === 0) return undefined;

    const targetsOf = () =>
      selectionTargets(blocksRef.current, selectedBlockId, multiSelectedRef.current);

    const inTextInput = () =>
      isTypingInField(
        multiSelectedRef.current.length > 0,
        selectedBlockId && findBlock(blocksRef.current, selectedBlockId)
      );

    // Native copy/cut (fires on Ctrl+C/X regardless of focus): write the
    // selected blocks synchronously through the event — more reliable than
    // the async clipboard API (no permission prompt, works from body focus)
    const onCopy = (e) => {
      if (inTextInput()) return;
      const targets = targetsOf();
      if (!targets.length) return;
      e.preventDefault();
      writeBlocksToClipboardData(e.clipboardData, targets);
    };
    const onCut = (e) => {
      if (inTextInput()) return;
      const targets = targetsOf();
      if (!targets.length) return;
      e.preventDefault();
      writeBlocksToClipboardData(e.clipboardData, targets);
      if (multiSelectedRef.current.length) deleteMultiSelected();
      else deleteSelected();
    };

    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        setSelectedBlockId(null);
        setMultiSelected([]);
        return;
      }
      if (inTextInput()) return;
      const targets = targetsOf();
      if (!targets.length) return;

      if ((e.ctrlKey || e.metaKey) && (e.key === 'c' || e.key === 'x')) {
        // After a drag-selection there is no text selection and no focused
        // editable, so the native copy/cut event never fires. Write the
        // clipboard directly instead.
        e.preventDefault();
        e.stopPropagation();
        if (!copyBlocksViaExecCommand(targets)) copyBlocksToClipboard(targets);
        if (e.key === 'x') {
          if (multiSelected.length) deleteMultiSelected();
          else deleteSelected();
        }
        return;
      }
      if (e.key === 'Tab') {
        const { handled, next, changed } = indentSelectedBlocks(
          blocksRef.current,
          targets,
          e.shiftKey ? -1 : 1
        );
        if (!handled) return;
        e.preventDefault();
        e.stopPropagation();
        if (changed) commit(next);
        return;
      }

      if (e.key !== 'Delete' && e.key !== 'Backspace') return;
      e.preventDefault();
      if (multiSelected.length) deleteMultiSelected();
      else deleteSelected();
    };
    // Capture phase: block selections must win over view-level clipboard
    // hotkeys (kanban/table/etc listen on window bubble)
    document.addEventListener('keydown', onKeyDown, true);
    document.addEventListener('copy', onCopy, true);
    document.addEventListener('cut', onCut, true);
    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      document.removeEventListener('copy', onCopy, true);
      document.removeEventListener('cut', onCut, true);
    };
  }, [
    selectedBlockId,
    multiSelected,
    deleteSelected,
    deleteMultiSelected,
    blocksRef,
    commit,
    multiSelectedRef,
    setSelectedBlockId,
    setMultiSelected,
  ]);

  useEffect(() => {
    if (!selectedBlockId) return undefined;
    const onClick = (e) => {
      if (e.target.closest(CLICK_AWAY_KEEP) || isLinkedRowChromeTarget(e.target)) return;
      setSelectedBlockId(null);
    };
    document.addEventListener('click', onClick);
    return () => document.removeEventListener('click', onClick);
  }, [selectedBlockId, setSelectedBlockId]);
}
